import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { take } from 'rxjs/operators';

import { AppState } from '../../../state/app.state';
import { fuzzySearch, clearFuzzySearch } from '../../../state/search/search.actions';
import {
  selectFuzzySearchResults,
  selectFuzzySearchLoading,
  selectFuzzySearchError
} from '../../../state/search/search.selectors';

// Import the workspace selection modal
import { WorkspaceSelectionModalComponent } from '../../advanced-search/companies/people/workspace-selection-modal/workspace-selection-modal.component';
import { Workspace } from '../../../models/workspace.model';

// Import search services
import { newSearch } from '../../../state/search/search.actions';
import { selectNewSearchSuccess, selectNewSearchFailure } from '../../../state/search/search.selectors';
import { AuthService } from '../../../services/auth.service';
import { SharedService } from '../../../services/shared.service';
import { PopupService } from '../../../services/popup.service';
import { Router } from '@angular/router';
import { EventTypes } from '../../../event-types';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-people-recommendations',
  standalone: true,
  imports: [CommonModule, WorkspaceSelectionModalComponent],
  templateUrl: './people-recommendations.component.html',
  styleUrl: './people-recommendations.component.scss'
})
export class PeopleRecommendationsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() companyName: string = '';
  @Input() reportSearch: any = null;
  @Input() company: any = null;

  recommendations: any[] = [];
  isLoading = false;
  error: any | null = null;

  // Modal state for workspace selection
  showWorkspaceModal = false;
  selectedPersonForReport: any = null;

  // Per-person "Generate Report" button state, keyed by the person's
  // linkedin_url. While a report is being generated the button is blocked
  // ("Generating..."); once the report exists we store its search id so the
  // button turns into "View Report".
  private reportStateByPerson = new Map<string, { generating: boolean; searchId?: number }>();

  private searchSubject = new Subject<string>();
  private subscriptions: Subscription[] = [];

  user: any;

  constructor(
    private store: Store<AppState>,
    private authService: AuthService,
    private sharedService: SharedService,
    private popupService: PopupService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Get user data
    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

    // Set up search with debouncing
    const searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((query) => this.performSearch(query));
    this.subscriptions.push(searchSubscription);

    // Listen for search results
    const resultsSubscription = this.store
      .select(selectFuzzySearchResults)
      .subscribe((searchResults) => {
        if (!searchResults || !searchResults.apollo_result) {
          this.recommendations = [];
        } else {
          // Get up to 5 recommendations
          this.recommendations = (searchResults.apollo_result.people || []).slice(0, 5);
        }
        this.isLoading = false;
      });
    this.subscriptions.push(resultsSubscription);

    // Listen for loading state
    const loadingSubscription = this.store
      .select(selectFuzzySearchLoading)
      .subscribe((loading) => {
        this.isLoading = loading;
      });
    this.subscriptions.push(loadingSubscription);

    // Listen for errors
    const errorSubscription = this.store
      .select(selectFuzzySearchError)
      .subscribe((err) => {
        this.error = err;
        if (err) {
          this.isLoading = false;
        }
      });
    this.subscriptions.push(errorSubscription);

    // Flip the "Generating..." button to "View Report" at the same moment the
    // "Report has finished" banner appears (status 2 / SHOW_REPORT_BANNER). The
    // report is opened in a NEW TAB, which is a fresh full page load: it can't
    // hit the report-route-reuse staleness, and its report page self-heals
    // (fills cards from live websocket events + re-fetches on true completion),
    // so opening at banner time still converges to a complete report without a
    // manual refresh. REPORT_FINISHED is kept as a secondary flip signal.
    const reportEventsSubscription = this.sharedService.eventEmitter.subscribe((event) => {
      if (!event) {
        return;
      }

      if (event.type === EventTypes.SHOW_REPORT_BANNER || event.type === EventTypes.REPORT_FINISHED) {
        const finishedId = Number(event.data?.report_id);
        if (finishedId) {
          this.markReportReady(finishedId);
        }
      } else if (event.type === EventTypes.SEARCH_ERROR) {
        const failedId = Number(event.data?.search_id);
        if (failedId) {
          this.markReportFailed(failedId);
        }
      }
    });
    this.subscriptions.push(reportEventsSubscription);

    // Initial search if company name is available
    if (this.companyName) {
      this.searchForRecommendations();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to company name changes
    if (changes['companyName'] && changes['companyName'].currentValue) {
      this.searchForRecommendations();
    }

    // Extract company name from reportSearch if not provided directly
    if (changes['reportSearch'] && changes['reportSearch'].currentValue) {
      this.extractCompanyName();
      this.searchForRecommendations();
    }

    // Extract company name from company object if available
    if (changes['company'] && changes['company'].currentValue) {
      this.extractCompanyNameFromCompany();
      this.searchForRecommendations();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    // Clear search results when component is destroyed
    this.store.dispatch(clearFuzzySearch());
  }

  private extractCompanyName(): void {
    if (this.reportSearch?.company?.name) {
      this.companyName = this.reportSearch.company.name;
    } else if (this.reportSearch?.search_person?.work_history?.[0]?.company_name) {
      this.companyName = this.reportSearch.search_person.work_history[0].company_name;
    }
  }

  private extractCompanyNameFromCompany(): void {
    if (this.company?.name) {
      this.companyName = this.company.name;
    }
  }

  private searchForRecommendations(): void {
    if (!this.companyName || this.companyName.trim().length < 5) {
      return;
    }

    // Use the search subject to trigger debounced search
    this.searchSubject.next(this.companyName.trim());
  }

  private performSearch(query: string): void {
    if (!query || query.length < 5) {
      this.recommendations = [];
      this.store.dispatch(clearFuzzySearch());
      return;
    }

    this.error = null;
    this.isLoading = true;
    this.store.dispatch(fuzzySearch({ query }));
  }

  selectPerson(person: any): void {
    // Optional: Handle person selection (could show more details, etc.)
    console.log('Selected person:', person);
  }

  generateReport(person: any, event: Event): void {
    // Prevent the parent click event from firing
    event.stopPropagation();

    if (!person.linkedin_url) {
      this.popupService.notify('LinkedIn URL not available for this person', 'Generate Report', 'error');
      return;
    }

    // Report already generated → the button now acts as "View Report".
    if (this.isReportReady(person)) {
      this.viewReport(person);
      return;
    }

    // A generation is already in flight for this person → ignore extra clicks.
    if (this.isGenerating(person)) {
      return;
    }

    // Block the button and start generating.
    this.reportStateByPerson.set(person.linkedin_url, { generating: true });
    this.processSearch(person.linkedin_url);
  }

  /** True while this person's report is being generated (button is blocked). */
  isGenerating(person: any): boolean {
    return !!this.reportStateByPerson.get(person?.linkedin_url)?.generating;
  }

  /** True once this person's report exists (button becomes "View Report"). */
  isReportReady(person: any): boolean {
    const state = this.reportStateByPerson.get(person?.linkedin_url);
    return !!state && !state.generating && !!state.searchId;
  }

  /**
   * Opens the already-generated report in a NEW TAB. A new tab is a fresh full
   * page load, so it sidesteps the report-route-reuse staleness and its report
   * page self-heals to a complete report — all while keeping the current report
   * open so the user can continue browsing recommendations.
   */
  viewReport(person: any): void {
    const searchId = this.reportStateByPerson.get(person?.linkedin_url)?.searchId;
    if (searchId) {
      const opened = window.open(`/report/${searchId}`, '_blank', 'noopener,noreferrer');
      // If a popup blocker prevented the new tab, fall back to in-tab navigation.
      if (!opened) {
        this.router.navigate(['/report', searchId]);
      }
    }
  }

  /**
   * Marks the person whose report is ready as "View Report". Triggered by the
   * report-finished banner event (matched on search id).
   */
  private markReportReady(searchId: number): void {
    this.reportStateByPerson.forEach((state, linkedinUrl) => {
      if (state.generating && state.searchId === searchId) {
        this.reportStateByPerson.set(linkedinUrl, { generating: false, searchId });
      }
    });
  }

  /**
   * Resets the person whose report failed during processing back to idle, so the
   * "Generate Report" button is offered again for a retry.
   */
  private markReportFailed(searchId: number): void {
    this.reportStateByPerson.forEach((state, linkedinUrl) => {
      if (state.searchId === searchId) {
        this.reportStateByPerson.delete(linkedinUrl);
      }
    });
  }

  private processSearch(linkedinUrl: string): void {
    // Store the current LinkedIn profile being searched
    const currentLinkedInProfile = linkedinUrl;

    // Generate the new report in the SAME workspace as the current report, so a
    // report opened inside a workspace keeps producing reports in that workspace.
    const workspaceId = this.reportSearch?.workspace_id ?? this.reportSearch?.workspace?.id;

    const searchData: any = {
      user_id: this.user.id,
      linkedin_profile: currentLinkedInProfile
    };
    if (workspaceId) {
      searchData.workspace_id = workspaceId;
    }

    // Dispatch new search action (same as search-person component)
    this.store.dispatch(newSearch(searchData));

    // selectNewSearchSuccess/Failure are GLOBAL (state.search.*) and shared with
    // other components (header search-person, dashboard, …). The success side is
    // keyed by linkedin_profile, but the error side carries no profile — so we
    // (a) tear BOTH subscriptions down on the first definitive outcome (otherwise
    // a never-erroring success leaves the failure sub alive forever — and since
    // "View Report" opens a new tab the component isn't destroyed, so they'd
    // accumulate), and (b) only let a global failure reset THIS button while this
    // person is genuinely still in-flight (generating, no search id yet).
    const outcomeSubs: Subscription[] = [];
    const settle = () => outcomeSubs.forEach(sub => sub.unsubscribe());

    const successSubscription = this.store.select(selectNewSearchSuccess)
      .pipe(
        filter(response => response !== null),
        filter(response => response.search?.linkedin_profile === currentLinkedInProfile),
        take(1)
      )
      .subscribe({
        next: (response) => {
          if (response.search && response.search.id) {
            // The search record now exists, but a freshly created search is still
            // PROCESSING (status 1). Keep the button blocked as "Generating..." and
            // remember its id so the report-finished banner event can flip it to
            // "View Report". An already-complete search (status 3, e.g. a re-scan)
            // is ready immediately.
            const alreadyComplete = response.search.search_status_id === 3;
            this.reportStateByPerson.set(currentLinkedInProfile, {
              generating: !alreadyComplete,
              searchId: response.search.id
            });
          } else {
            // No usable search id → reset so the user can retry.
            this.reportStateByPerson.delete(currentLinkedInProfile);
          }
          settle();
        },
        error: () => {
          this.reportStateByPerson.delete(currentLinkedInProfile);
          this.popupService.notify('Failed to generate report', 'Search Error', 'error');
          settle();
        }
      });

    const failureSubscription = this.store.select(selectNewSearchFailure)
      .pipe(
        filter(failure => failure !== null),
        // Only act while THIS person is still in-flight (the global error field is
        // not keyed to a profile; ignore failures meant for other searches).
        filter(() => {
          const state = this.reportStateByPerson.get(currentLinkedInProfile);
          return !!state?.generating && state.searchId === undefined;
        }),
        take(1)
      )
      .subscribe({
        next: (error) => {
          const errorMessage = error.error?.message || 'Failed to generate report';
          // Unblock the button so the user can try again.
          this.reportStateByPerson.delete(currentLinkedInProfile);
          this.popupService.notify(errorMessage, 'Search Error', 'error');
          settle();
        }
      });

    outcomeSubs.push(successSubscription, failureSubscription);
    this.subscriptions.push(successSubscription, failureSubscription);
  }

  // Modal event handlers
  onReportGenerated(workspace: Workspace): void {
    if (this.selectedPersonForReport && this.user) {
      const linkedinUrl = this.selectedPersonForReport.linkedin_url;

      // Close modal
      this.showWorkspaceModal = false;

      // Show loading banner
      this.sharedService.emitEvent(EventTypes.SHOW_LOADING_REPORT_BANNER, {
        message: 'Loading Report Banner'
      });

      // Start search with selected workspace
      this.commitSearch(linkedinUrl, workspace.id);

      // Reset modal state
      this.selectedPersonForReport = null;
    }
  }

  onModalClosed(): void {
    this.showWorkspaceModal = false;
    this.selectedPersonForReport = null;
  }

  private commitSearch(linkedinUrl: string, workspaceId?: string): void {
    const searchData: any = {
      user_id: this.user.id,
      linkedin_profile: linkedinUrl
    };

    if (workspaceId) {
      searchData.workspace_id = workspaceId;
    }

    this.store.dispatch(newSearch(searchData));

    // Listen for search success
    const successSubscription = this.store.select(selectNewSearchSuccess)
      .pipe(
        filter(response => response !== null),
        filter(response => response.search?.linkedin_profile === linkedinUrl),
        take(1)
      )
      .subscribe({
        next: (response) => {
          if (response.search && response.search.search_status_id) {
            // Only close the banner if the report is already completed (status 3)
            // If it's still being processed (status !== 3), leave the banner open
            // so the "Report has finished" banner can show when the WebSocket event arrives
            if (response.search.search_status_id === 3) {
              this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, {
                message: 'Close Report Banner'
              });
            }

            // Navigate to the report page
            this.router.navigate(['/report', response.search.id]);
          }
        },
        error: (error) => {
          this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, {
            message: 'Close Report Banner'
          });
          this.popupService.notify('Failed to generate report', 'Search Error', 'error');
        }
      });

    this.subscriptions.push(successSubscription);

    // Listen for search failure
    const failureSubscription = this.store.select(selectNewSearchFailure)
      .pipe(
        filter(failure => failure !== null),
        take(1)
      )
      .subscribe({
        next: (error) => {
          const errorMessage = error.error?.message || 'Failed to generate report';
          this.popupService.notify(errorMessage, 'Search Error', 'error');
          this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, {
            message: 'Close Report Banner'
          });
        }
      });

    this.subscriptions.push(failureSubscription);
  }

  getPersonAvatar(person: any): string {
    // Check if photo_url exists, is not empty array string, and is not LinkedIn default placeholder
    if (person.photo_url && person.photo_url !== '[]' && !this.isLinkedInDefaultAvatar(person.photo_url)) {
      return person.photo_url;
    }
    // Fallback to profile_picture if available and not LinkedIn default placeholder
    if (person.profile_picture && person.profile_picture !== '[]' && !this.isLinkedInDefaultAvatar(person.profile_picture)) {
      return person.profile_picture;
    }
    // Generate default avatar with person's name
    return this.getDefaultAvatar(person.name);
  }

  private isLinkedInDefaultAvatar(url: string): boolean {
    // Check if the URL is the LinkedIn default placeholder avatar
    return url && url.includes('https://static.licdn.com/aero-v1/sc/h/9c8pery4andzj6ohjkjp54ma2');
  }

  getDefaultAvatar(name: string): string {
    return `https://ui-avatars.com/api/?background=FFA500&color=fff&rounded=true&name=${encodeURIComponent(name)}&format=svg&size=128`;
  }

  getPersonCompanyLogo(person: any): string {
    const organization = person.organization;
    if (!organization) {
      return this.getDefaultCompanyLogo('Unknown Company');
    }

    if (organization.logo_url == null || organization.logo_url === '[]') {
      return this.getDefaultCompanyLogo(organization.name || 'Unknown Company');
    }

    return organization.logo_url;
  }

  getDefaultCompanyLogo(companyName: string): string {
    return `https://ui-avatars.com/api/?background=FFA500&color=fff&rounded=true&name=${encodeURIComponent(companyName)}&format=svg&size=128`;
  }

  onImageError(event: Event, name: string): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = this.getDefaultAvatar(name);
  }

  onCompanyLogoError(event: Event, companyName: string): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = this.getDefaultCompanyLogo(companyName);
  }
}
